import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader,
  AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { Loader2, Trash2, ShieldAlert, RefreshCw } from "lucide-react";
import { toast } from "sonner";

export default function Maintenance() {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [purging, setPurging] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/admin/demo-data/preview")
      .then((r) => setPreview(r.data))
      .catch(() => toast.error("Impossible de charger l'aperçu"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const purge = () => {
    setPurging(true);
    api.post("/admin/demo-data/purge")
      .then((r) => {
        const d = r.data.deleted || {};
        toast.success(`Nettoyage effectué : ${d.clients || 0} clients et ${d.orders || 0} commandes supprimés`);
        load();
      })
      .catch((e) => toast.error(apiError(e.response?.data?.detail) || "Échec du nettoyage"))
      .finally(() => setPurging(false));
  };

  const nothing = preview && preview.clients === 0 && preview.orders === 0;

  return (
    <div className="space-y-6" data-testid="maintenance-page">
      <div>
        <h1 className="text-2xl font-bold font-heading text-slate-900">Maintenance</h1>
        <p className="text-slate-500 text-sm mt-1">Outils d'administration de la base de données.</p>
      </div>

      <div className="bg-white border rounded-2xl p-6 max-w-2xl">
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-xl bg-amber-50 text-amber-600"><ShieldAlert className="w-5 h-5" /></div>
          <div className="flex-1">
            <h2 className="text-lg font-semibold text-slate-900">Purger les données de démonstration</h2>
            <p className="text-sm text-slate-500 mt-1">
              Supprime uniquement les enregistrements clairement marqués démo/test
              (clients de démonstration REF-001…004, noms commençant par « TEST_ », données d'injection)
              ainsi que leurs commandes, paiements, reçus, réclamations et abonnements liés.
              <strong className="text-slate-700"> Vos vraies commandes et clients ne sont jamais touchés.</strong>
            </p>

            <div className="mt-4 rounded-xl bg-slate-50 border p-4">
              {loading ? (
                <div className="flex items-center gap-2 text-slate-500 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Analyse…</div>
              ) : preview ? (
                <div className="text-sm">
                  <div className="flex gap-6">
                    <div><span className="text-slate-400">Clients ciblés :</span> <span data-testid="preview-clients" className="font-bold text-slate-800">{preview.clients}</span></div>
                    <div><span className="text-slate-400">Commandes ciblées :</span> <span data-testid="preview-orders" className="font-bold text-slate-800">{preview.orders}</span></div>
                  </div>
                  {preview.client_names?.length > 0 && (
                    <div className="mt-2 text-xs text-slate-500">{preview.client_names.join(" · ")}</div>
                  )}
                </div>
              ) : null}
            </div>

            <div className="flex items-center gap-2 mt-4">
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button data-testid="purge-demo-btn" disabled={loading || purging || nothing} className="bg-red-600 hover:bg-red-700">
                    {purging ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
                    Purger les données de démo
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Confirmer la purge</AlertDialogTitle>
                    <AlertDialogDescription>
                      {preview ? `${preview.clients} client(s) et ${preview.orders} commande(s) de démonstration seront définitivement supprimés, avec leurs données liées. Cette action est irréversible.` : ""}
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel data-testid="purge-cancel">Annuler</AlertDialogCancel>
                    <AlertDialogAction data-testid="purge-confirm" onClick={purge} className="bg-red-600 hover:bg-red-700">
                      Oui, supprimer
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>

              <Button data-testid="preview-refresh" variant="outline" onClick={load} disabled={loading || purging}>
                <RefreshCw className="w-4 h-4 mr-2" /> Actualiser
              </Button>
            </div>

            {nothing && !loading && (
              <p className="text-sm text-emerald-600 mt-3">✓ Aucune donnée de démonstration détectée — la base est propre.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
